import { api } from './client'

/**
 * F-090 (TASK-0344): aggregati di reporting dei lead (funnel giornaliero, attivita' operatori) e prestazioni per
 * operatore. Stato, riconciliazione e ricalcolo sono solo Admin; `operator-performance` e' per i Manager.
 * Nessun importo in questi payload: sono conteggi.
 */

/** Da dove vengono i numeri di un report: aggregato giornaliero oppure calcolo dal vivo. */
export type ReportDataSource = 'aggregate' | 'live'

/** I due campi che i report lead generation e call center aggiungono alla risposta. */
export interface WithDataSource {
  dataSource?: ReportDataSource
  /** Per "aggregate": il calcolo piu' vecchio dei giorni del periodo (il dato e' aggiornato almeno a quel momento). */
  computedAt?: string | null
}

/** Testo e suggerimento del badge "fonte dati" dei report (stesso aspetto in tutte le pagine). */
export function dataSourceBadge(source: ReportDataSource | undefined, computedAt: string | null | undefined):
  { text: string; title: string; aggregate: boolean } | null {
  if (!source) return null
  if (source === 'live') {
    return { text: 'Calcolo dal vivo', title: 'I numeri sono calcolati al momento sui dati di origine.', aggregate: false }
  }
  const when = computedAt
    ? new Date(computedAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  return {
    text: 'Aggregato giornaliero',
    title: when
      ? `I numeri vengono dagli aggregati giornalieri, aggiornati almeno al ${when}.`
      : 'I numeri vengono dagli aggregati giornalieri.',
    aggregate: true,
  }
}

export interface AggregateTableStatus {
  table: string
  rows: number
  daysCovered: number
  lastComputedAt: string | null
}

export interface ReportingAggregatesStatus {
  schedulerEnabled: boolean
  readFromAggregates: boolean
  tables: AggregateTableStatus[]
  calculatedDays: {
    count: number
    from: string | null
    to: string | null
    lastComputedAt: string | null
  }
  note: string
}

export interface ReconciliationDifference {
  kind: 'funnel' | 'operator'
  key: string
  field: string
  aggregate: number | null
  live: number | null
}

export interface LeadFunnelReconciliation {
  day: string
  /** false: il giorno non e' mai stato calcolato, quindi non c'e' nulla da confrontare. */
  calculated: boolean
  matches: boolean
  aggregateRows: number
  liveRows: number
  differences: ReconciliationDifference[]
}

export interface OperatorPerformanceItem {
  day: string
  operatorId: number | null
  contractsCount: number
  activationsCount: number
}

export interface OperatorPerformance {
  from: string
  to: string
  totals: { contractsCount: number; activationsCount: number }
  items: OperatorPerformanceItem[]
  note: string
}

export const reportingAggregatesApi = {
  status: () => api<ReportingAggregatesStatus>('/api/reports/aggregates/status'),
  reconcile: (day: string) =>
    api<LeadFunnelReconciliation>(`/api/reports/aggregates/lead-funnel/reconciliation?day=${encodeURIComponent(day)}`),
  recalculate: (day: string) =>
    api<{ day: string; enqueued: number }>(`/api/reports/aggregates/recalculate?day=${encodeURIComponent(day)}`, { method: 'POST' }),
  operatorPerformance: (from?: string, to?: string) => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    const s = p.toString()
    return api<OperatorPerformance>(`/api/reports/aggregates/operator-performance${s ? `?${s}` : ''}`)
  },
}
